import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { searchLocations } from '../services/api';
import { useApp } from '../context/AppContext';

// Ein Filter-Set, zwei Verbraucher (Vorschläge + Suche). Die Umrechnung auf
// die Query-Parameter des Backends steht nur hier — die Namen stammen aus
// backend/controllers/jobsController.js (buildJobFilters).

export const DEFAULT_RADIUS_KM = 10;

export const EMPTY_FILTERS = {
  jobTypes: [],
  branchen: [],
  weekdays: [],
  daytimes: [],
  daysMin: '', daysMax: '',
  hoursMin: '', hoursMax: '',
  payMin: '',
  startDate: '',
  noExperience: false,
  noLicense: false,
  remote: false,
  location: '', locationId: null, radius: DEFAULT_RADIUS_KM,
  sort: 'neueste',
  onlyMyAge: false,
};

// Alter aus dem Profil. Für Altbestände mit nur birth_year gilt der 31.12. —
// gleiche konservative Annahme wie im Backend (isMinorSql).
export const ageOf = (user) => {
  const birth = user?.birth_date
    ? new Date(user.birth_date)
    : (user?.birth_year ? new Date(Number(user.birth_year), 11, 31) : null);
  if (!birth || Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const hadBirthday =
    today.getMonth() > birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() >= birth.getDate());
  if (!hadBirthday) age -= 1;
  return age;
};

export const filtersToParams = (f, user) => {
  const p = {};
  if (f.jobTypes.length) p.job_type = f.jobTypes;
  if (f.branchen.length) p.branche = f.branchen;
  if (f.weekdays.length) p.weekdays = f.weekdays;
  if (f.daytimes.length) p.daytime = f.daytimes;
  if (f.daysMin !== '') p.days_per_week_min = f.daysMin;
  if (f.daysMax !== '') p.days_per_week_max = f.daysMax;
  if (f.hoursMin !== '') p.hours_per_week_min = f.hoursMin;
  if (f.hoursMax !== '') p.hours_per_week_max = f.hoursMax;
  if (f.payMin !== '') p.pay_per_hour_min = f.payMin;
  if (f.startDate) p.start_date = f.startDate;
  if (f.noExperience) p.no_experience_required = true;
  if (f.noLicense) p.drivers_license_required = false;
  if (f.remote) p.remote_possible = true;
  // Umkreis braucht die location_id als Mittelpunkt; ohne Auswahl bleibt es
  // beim Textabgleich auf dem Ortsnamen
  if (f.locationId != null) {
    p.location_id = f.locationId;
    if (f.radius) p.radius_km = f.radius;
  } else if (f.location) {
    p.location = f.location;
  }
  if (f.sort) p.sort = f.sort;

  const age = ageOf(user);
  if (f.onlyMyAge && age != null) p.max_min_age = age;
  return p;
};

// Umkehrung von filtersToParams für die gespeicherten Wünsche
// (youth_profiles.preferences hat die Form der Query-Parameter). Ort, Umkreis,
// Sortierung und Altersfilter sind keine Wünsche und bleiben leer.
export const paramsToFilters = (p) => ({
  ...EMPTY_FILTERS,
  jobTypes: p?.job_type || [],
  branchen: p?.branche || [],
  weekdays: p?.weekdays || [],
  daytimes: p?.daytime || [],
  daysMin: p?.days_per_week_min ?? '', daysMax: p?.days_per_week_max ?? '',
  hoursMin: p?.hours_per_week_min ?? '', hoursMax: p?.hours_per_week_max ?? '',
  payMin: p?.pay_per_hour_min ?? '',
  startDate: p?.start_date || '',
  noExperience: !!p?.no_experience_required,
  noLicense: p?.drivers_license_required === false,
  remote: !!p?.remote_possible,
  sort: '',
});

// Zusammengehörige Felder zählen als ein Filter (Bereich = ein Filter,
// Ort + location_id auch). sort ist kein Filter.
export const FILTER_GROUPS = [
  { keys: ['jobTypes'], label: 'Jobart' },
  { keys: ['branchen'], label: 'Branche' },
  { keys: ['weekdays'], label: 'Arbeitstage' },
  { keys: ['daytimes'], label: 'Tageszeit' },
  { keys: ['daysMin', 'daysMax'], label: 'Tage pro Woche' },
  { keys: ['hoursMin', 'hoursMax'], label: 'Stunden pro Woche' },
  { keys: ['payMin'], label: 'Mindest-Stundenlohn' },
  { keys: ['startDate'], label: 'Verfügbar ab' },
  { keys: ['noExperience'], label: 'Keine Vorkenntnisse nötig' },
  { keys: ['noLicense'], label: 'Kein Führerschein nötig' },
  { keys: ['remote'], label: 'Homeoffice möglich' },
  { keys: ['location', 'locationId'], label: 'Ort' },
  { keys: ['radius'], label: 'Umkreis' },
  { keys: ['onlyMyAge'], label: 'Altersfilter' },
];

export const isGroupActive = (group, f, base = EMPTY_FILTERS) =>
  group.keys.some(key => JSON.stringify(f[key]) !== JSON.stringify(base[key]));

export const countActiveFilters = (f, base = EMPTY_FILTERS) =>
  FILTER_GROUPS.filter(group => isGroupActive(group, f, base)).length;

export function useJobFilters() {
  const { user } = useApp();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  // Stand nach Vorbelegung (Wohnort, Altersfilter) = "keine eigenen Filter"
  const base = useRef(EMPTY_FILTERS);
  const [isMinor, setIsMinor] = useState(null);

  const setDefaults = useCallback((patch) => {
    base.current = { ...base.current, ...patch };
    setFilters(prev => ({ ...prev, ...patch }));
  }, []);

  // Wohnort aus dem Profil vorbelegen. youth_profiles speichert nur den
  // Ortsnamen, die location_id kommt aus /locations/search.
  const prefilled = useRef(false);
  useEffect(() => {
    const home = user?.youth_location;
    if (prefilled.current || !home) return;
    prefilled.current = true;

    let cancelled = false;
    searchLocations(home, 1)
      .then(({ data }) => {
        const hit = (Array.isArray(data) ? data : data?.locations || [])[0];
        if (cancelled || !hit) return;
        base.current = { ...base.current, location: hit.name, locationId: hit.id };
        // Hat der Nutzer inzwischen selbst einen Ort gewählt, bleibt der stehen
        setFilters(prev => (prev.location ? prev : { ...prev, location: hit.name, locationId: hit.id }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.youth_location]);

  // Der Such-Endpoint meldet mit, ob der eingeloggte Nutzer minderjährig ist.
  // Dann ist der Altersfilter Standard (Jugendarbeitsschutz) — einmal setzen,
  // danach entscheidet der Nutzer.
  const ageApplied = useRef(false);
  const noteIsMinor = useCallback((value) => {
    setIsMinor(value ?? null);
    if (value && !ageApplied.current) {
      ageApplied.current = true;
      setDefaults({ onlyMyAge: true });
    }
  }, [setDefaults]);

  const resetFilters = useCallback(() => setFilters(base.current), []);

  const params = useMemo(() => filtersToParams(filters, user), [filters, user]);
  const activeCount = countActiveFilters(filters, base.current);

  return { filters, setFilters, resetFilters, defaults: base.current, params, activeCount, isMinor, noteIsMinor };
}
